import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useToast } from '../contexts/ToastContext';

const HubungiKami = () => {
  const websiteName = process.env.REACT_APP_WEBSITE_NAME || 'LPIPM Politeknik Bisnis Digital Indonesia';
  const address = process.env.REACT_APP_ADDRESS || 'Jl. Contoh No. 123, Jakarta';
  const phone = process.env.REACT_APP_PHONE || '(021) 1234-5678';
  const email = process.env.REACT_APP_EMAIL || 'lpipm@polibis.ac.id';
  const { addToast } = useToast();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const data = new FormData();
      Object.keys(formData).forEach((key) => data.append(key, formData[key]));
      if (file) data.append('attachment', file);

      const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/contact`, {
        method: 'POST',
        body: data,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to send message');
      }

      addToast({
        type: 'success',
        message: 'Pesan berhasil dikirim!',
      });

      // Reset form
      setFormData({ name: '', email: '', subject: '', message: '' });
      setFile(null);
    } catch (error) {
      console.error(error);
      addToast({
        type: 'error',
        message: error.message || 'Gagal mengirim pesan. Silakan coba lagi nanti.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Hubungi Kami | {websiteName}</title>
        <meta name="description" content="Hubungi kami untuk informasi lebih lanjut tentang LPIPM Politeknik Bisnis Digital Indonesia" />
      </Helmet>

      <div className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Hubungi Kami</h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Kami siap membantu Anda dengan pertanyaan atau informasi yang Anda butuhkan.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            {/* Informasi Kontak */}
            <div className="space-y-8">
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Informasi Kontak</h2>

                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-dark-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-dark-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900 mb-1">Alamat</h3>
                      <p className="text-gray-600">{address}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-dark-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-dark-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900 mb-1">Telepon</h3>
                      <a href={`tel:${phone}`} className="text-gray-600 hover:text-dark-blue-700 transition-colors">{phone}</a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-dark-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-dark-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900 mb-1">Email</h3>
                      <a href={`mailto:${email}`} className="text-gray-600 hover:text-dark-blue-700 transition-colors">{email}</a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Form Kontak */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Kirim Pesan & Dokumen Buku</h2>
              <p className="text-gray-600 mb-6">
                Anda bisa mengirimkan dokumen buku atau pesan kepada kami melalui form berikut.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nama</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-dark-blue-500 focus:border-dark-blue-500 outline-none transition-all"
                    placeholder="Masukkan nama Anda"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-dark-blue-500 focus:border-dark-blue-500 outline-none transition-all"
                    placeholder="Masukkan email Anda"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subjek</label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-dark-blue-500 focus:border-dark-blue-500 outline-none transition-all"
                    placeholder="Contoh: Pengiriman Naskah Buku"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pesan</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-dark-blue-500 focus:border-dark-blue-500 outline-none transition-all resize-none"
                    placeholder="Tulis pesan atau deskripsi tentang dokumen buku yang Anda kirim..."
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Lampiran Dokumen Buku (PDF/JPG/JPEG, max 10MB)</label>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg"
                    onChange={handleFileChange}
                    className="w-full"
                  />
                  {file && (
                    <p className="text-sm text-gray-600 mt-2">
                      File terpilih: {file.name}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full px-6 py-3 bg-dark-blue-700 text-white rounded-xl font-medium hover:bg-dark-blue-800 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Mengirim...' : 'Kirim Pesan'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HubungiKami;
